import api from "../api";
import { getCurrentMonthDateRange } from "@utils/dateRange";
import {
  DashboardFilter,
  DashboardRequestPayload,
  FinancialEvolution,
  ExpenseCategory,
  RevenueCategory,
  KPIData,
  DREHierarchicalResponse,
  DREExpandRequest,
  DREExpandResponse,
} from "../../types/dashboard";

const buildRequestPayload = (filters?: DashboardFilter): DashboardRequestPayload => {
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getCurrentMonthDateRange();

  const payload: DashboardRequestPayload = {
    accountId: "",
    startDate: filters?.startDate || defaultStartDate,
    endDate: filters?.endDate || defaultEndDate,
  };

  // Filtros por LISTA de IDs (novo - multi-select)
  if (filters?.branchIds?.length) payload.branchIds = filters.branchIds;
  if (filters?.segmentIds?.length) payload.segmentIds = filters.segmentIds;
  if (filters?.clientIds?.length) payload.clientIds = filters.clientIds;
  if (filters?.vehicleIds?.length) payload.vehicleIds = filters.vehicleIds;
  if (filters?.driverIds?.length) payload.driverIds = filters.driverIds;

  // Filtros por nome (backward compatibility - backend faz busca ILIKE)
  if (filters?.filial) payload.filial = filters.filial;
  if (filters?.segmento) payload.segmento = filters.segmento;
  if (filters?.cliente) payload.cliente = filters.cliente;
  if (filters?.veiculo) payload.veiculo = filters.veiculo;
  if (filters?.motorista) payload.motorista = filters.motorista;

  return payload;
};

export const dashboardService = {
  getKPI: async (filters?: DashboardFilter): Promise<KPIData> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/summary", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching KPI:", error);
      throw error;
    }
  },

  getDRE: async (filters?: DashboardFilter): Promise<DREHierarchicalResponse> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/dre/hierarchical", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching DRE:", error);
      throw error;
    }
  },

  getFinancialEvolution: async (filters?: DashboardFilter): Promise<FinancialEvolution[]> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/financial-evolution", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching financial evolution:", error);
      throw error;
    }
  },

  getFinancialEvolutionLast12Months: async (filters?: DashboardFilter): Promise<FinancialEvolution[]> => {
    try {
      const now = new Date();
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const startDate = new Date(now.getFullYear(), now.getMonth() - 11, 1);

      const filtersWithLast12Months: DashboardFilter = {
        ...filters,
        startDate: startDate.toISOString().split("T")[0],
        endDate: endDate.toISOString().split("T")[0],
      };

      const payload = buildRequestPayload(filtersWithLast12Months);
      const response = await api.post("/dashboard/financial-evolution", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching financial evolution (last 12 months):", error);
      throw error;
    }
  },

  getExpenses: async (filters?: DashboardFilter): Promise<any> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/expenses", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching expenses:", error);
      throw error;
    }
  },

  getRevenues: async (filters?: DashboardFilter): Promise<any> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/revenues", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching revenues:", error);
      throw error;
    }
  },

  getExpenseCategories: async (filters?: DashboardFilter): Promise<ExpenseCategory[]> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/expense/categories", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching expense categories:", error);
      throw error;
    }
  },

  getRevenueCategories: async (filters?: DashboardFilter): Promise<RevenueCategory[]> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/revenue/categories", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching revenue categories:", error);
      throw error;
    }
  },

  expandDRENode: async (filters: DashboardFilter, expandRequest: DREExpandRequest): Promise<DREExpandResponse> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/dre/expand", {
        filters: payload,
        expandRequest,
      });
      return response.data;
    } catch (error) {
      console.error("Error expanding DRE node:", error);
      throw error;
    }
  },

  exportDRE: async (
    filters?: DashboardFilter,
    viewType?: "general" | "branch" | "segment" | "vehicle" | "driver"
  ): Promise<void> => {
    try {
      const payload = buildRequestPayload(filters);

      const viewMap = {
        general: "GERAL",
        branch: "FILIAL",
        segment: "SEGMENTO",
        vehicle: "VEICULO",
        driver: "MOTORISTA",
      };

      const body = {
        view: viewMap[viewType || "general"],
        startDate: payload.startDate,
        endDate: payload.endDate,
      };

      const response = await api.post("/dre/export-xlsx", body, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `DRE_${payload.startDate}_${payload.endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting DRE:", error);
      throw error;
    }
  },
};

export default dashboardService;
